import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { checkIfPageEnabled } from '../../AppHelpers';
import { isPublicPlatform as isPublicPlatformFn } from '../../utils/publicPlatforms';
import {
  useConfigStore, useAppStore, useWindowStore, useAuthStore,
} from '../../stores';

import FadingContainer from '../FadingContainer/FadingContainer';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import PageWrapper from '../PageWrapper/PageWrapper';

const AppSwitchboard = ({
  isAuthed,
  isLoggedIn,
  isLoggingIn,
}) => {
  const [allowPageAccess, setAllowPageAccess] = useState(false);
  const [isWidthError, setIsWidthError] = useState(false);
  const [widthCheckAttempted, setWidthCheckAttempted] = useState(false);
  const [isAccessError, setIsAccessError] = useState(false);
  const [isCommunityAccessError, setIsCommunityAccessError] = useState(false);
  const [isAuthError, setIsAuthError] = useState(false);
  const [accessCheckAttempted, setAccessCheckAttempted] = useState(false);
  const [communityAccessCheckAttempted, setCommunityAccessCheckAttempted] = useState(false);
  const [authCheckAttempted, setAuthCheckAttempted] = useState(false);

  // Zustand stores
  const config = useConfigStore((state) => state.config);
  const featureFlags = useConfigStore((state) => state.featureFlags);
  const settings = useAppStore((state) => state.settings);
  const appStatus = useAppStore((state) => state.appStatus);
  const setAppStatus = useAppStore((state) => state.setAppStatus);
  const isMobile = useWindowStore((state) => state.isMobile);

  // Community access check from auth store (OG membership)
  const accessChecked = useAuthStore((state) => state.accessChecked);
  const hasAccess = useAuthStore((state) => state.hasAccess);
  const accessDeniedMessage = useAuthStore((state) => state.accessDeniedMessage);
  // Get user info for error messages

  const { enableKioskMode, enableSiteSee } = config || {};
  const { platform } = settings || {};

  const { allowLogin } = featureFlags || {};

  // Is the screen wide enough for the experience?
  useEffect(() => {
    if (isMobile) {
      // These platforms bypass the width restriction
      const bypassWidthCheck = ['page', 'mapTiles', 'siteSee', 'mapOnly'];
      if (!bypassWidthCheck.includes(platform)) {
        setIsWidthError(true);
      }
    } else {
      setIsWidthError(false);
    }
    setWidthCheckAttempted(true);
  }, [isMobile, platform]);

  // Is this page enabled in the config?
  useEffect(() => {
    const pageAccess = checkIfPageEnabled(
      platform,
      enableKioskMode,
      enableSiteSee,
    );
    setAllowPageAccess(pageAccess);
    setAccessCheckAttempted(true);
  }, [platform, enableKioskMode, enableSiteSee]);

  // If the no login is needed and the experience is authed, set status to
  // operational.
  useEffect(() => {
    // If access was explicitly denied by the server (user is logged in but no access),
    // we can skip auth check since we'll show the access denied error anyway
    if (accessChecked && !hasAccess) {
      setIsAuthError(false);
      setAuthCheckAttempted(true);
      return;
    }

    // Needed so we don't have a flash of an incorrect error on load
    if (platform === undefined || platform === null) return;

    // For YT platform, if we don't have allowLogin yet (config API failed or still loading),
    // and user is not logged in, assume login is required (this will redirect to login)
    // This handles the case where config API returns 403 for non-logged-in users
    const effectiveAllowLogin = allowLogin !== undefined
      ? allowLogin
      : (platform === 'yt');

    // Still waiting for allowLogin for non-yt platforms
    if (allowLogin === undefined && platform !== 'yt') return;

    if (!effectiveAllowLogin && isAuthed) {
      setIsAuthError(false);
      setAuthCheckAttempted(true);
    } else if (effectiveAllowLogin && isLoggedIn) {
      setIsAuthError(false);
      setAuthCheckAttempted(true);
    } else if (effectiveAllowLogin && !isLoggedIn && isLoggingIn) {
      // User is being redirected to login - show loading, not error
      setIsAuthError(false);
      setAuthCheckAttempted(true);
    } else if (effectiveAllowLogin && !isLoggedIn && !isLoggingIn) {
      // User needs to log in but redirect hasn't started yet
      // Don't complete auth check yet - wait for isLoggingIn to become true
      // This prevents flash of error screen before redirect

    } else {
      setIsAuthError(true);
      setAuthCheckAttempted(true);
    }
  }, [allowLogin,
    isAuthed,
    isLoggedIn,
    isLoggingIn,
    platform,
    accessChecked,
    hasAccess]);

  // Do we need to show an access error?
  useEffect(() => {
    setIsAccessError(!allowPageAccess);
  }, [allowPageAccess]);

  // Platforms that are public and should always have access
  const isPublicPlatform = isPublicPlatformFn(platform);

  // Check community access (OG membership) - only for logged-in YT users
  useEffect(() => {
    // FIRST: Check if access was explicitly denied (e.g., config API returned 403)
    // This takes priority over all other checks
    if (accessChecked && !hasAccess) {
      setCommunityAccessCheckAttempted(true);
      setIsCommunityAccessError(true);
      return;
    }

    // Public platforms always have access - skip any access checks
    if (isPublicPlatform) {
      setCommunityAccessCheckAttempted(true);
      setIsCommunityAccessError(false);
      return;
    }

    // For non-YT platforms or when login is not required, grant access
    // Note: allowLogin might be undefined if config failed, so check explicitly for false
    if (platform !== 'yt' || allowLogin === false) {
      setCommunityAccessCheckAttempted(true);
      setIsCommunityAccessError(false);
      return;
    }

    // Wait for access check to complete
    if (!accessChecked) {
      return;
    }

    setCommunityAccessCheckAttempted(true);
    setIsCommunityAccessError(!hasAccess);
  }, [platform, allowLogin, accessChecked, hasAccess, isPublicPlatform]);

  // What status is the app in?
  useEffect(() => {
    if (platform === undefined || platform === null) {
      setAppStatus('loggedOut');
    }

    if (
      widthCheckAttempted
      && authCheckAttempted
      && accessCheckAttempted
      && communityAccessCheckAttempted
    ) {
      // If user is in the process of logging in, show loading screen
      // This prevents flash of error screen during redirect to Drupal
      if (isLoggingIn) {
        setAppStatus('loading');
      } else if (isWidthError
        || isAuthError
        || isAccessError
        || isCommunityAccessError
      ) {
        setAppStatus('error');
      } else {
        setAppStatus('operational');
      }
    }
  }, [
    platform,
    widthCheckAttempted,
    isWidthError,
    authCheckAttempted,
    isAuthError,
    accessCheckAttempted,
    isAccessError,
    communityAccessCheckAttempted,
    isCommunityAccessError,
    isLoggingIn,
    setAppStatus,
  ]);

  // For PIB page (platform 'page'), render immediately with skeletons
  let component = platform === 'page' ? <PageWrapper /> : <Loading />;
  if (widthCheckAttempted && authCheckAttempted && accessCheckAttempted
    && communityAccessCheckAttempted) {
    switch (appStatus) {
      case 'loading': component = platform === 'page' ? <PageWrapper /> : <Loading />; break;
      case 'error': component = (
        <Error
          isWidthError={isWidthError}
          isAuthError={isAuthError}
          isAccessError={isAccessError}
          isCommunityAccessError={isCommunityAccessError}
          communityAccessMessage={accessDeniedMessage}
        />
      );
        break;
      case 'operational': component = <PageWrapper />; break;
      default: component = platform === 'page' ? <PageWrapper /> : <Loading />; break;
    }
  }

  return (
    <FadingContainer>
      {component}
    </FadingContainer>
  );
};

AppSwitchboard.propTypes = {
  isAuthed: PropTypes.bool,
  isLoggedIn: PropTypes.bool,
  isLoggingIn: PropTypes.bool,
};

AppSwitchboard.defaultProps = {
  isAuthed: false,
  isLoggedIn: false,
  isLoggingIn: false,
};

const Error = ({
  isWidthError,
  isAuthError,
  isAccessError,
  isCommunityAccessError,
  communityAccessMessage,
}) => {
  let message = 'Error';
  if (isWidthError) {
    message = (
      <p>
        This experience requires a window width of 768px or greater.
        Please increase the size of your window or view on a larger
        device.
      </p>
    );
  } else if (isAuthError) {
    message = (
      <p>
        You are not authorized to use YourTour.
        {' '}
        If you feel this is in error, please contact The Vectre at
        {' '}
        <a className="noAppMessage__minorBtn" href="mailto:support@thevectre.com">
          support@thevectre.com
        </a>
        .
      </p>
    );
  } else if (isCommunityAccessError) {
    // The API returns the full message with username and community name
    message = (
      <p>
        {communityAccessMessage || 'You do not have access to this community.'}
        {' '}
        If you feel this is in error, please contact The Vectre at
        {' '}
        <a className="noAppMessage__minorBtn" href="mailto:support@thevectre.com">
          support@thevectre.com
        </a>
        .
      </p>
    );
  } else if (isAccessError) {
    message = (
      <p>
        You do not have access to this page.
        {' '}
        If you feel this is in error, please contact The Vectre at
        {' '}
        <a href="mailto:support@thevectre.com">support@thevectre.com</a>
        .
      </p>
    );
  }
  return (
    <div className="content content--center">
      <div className="noAppMessage">
        {message}
      </div>
    </div>
  );
};

const Loading = () => (
  <div className="content content--center">
    <div className="noAppMessage">
      <LoadingSpinner />
    </div>
  </div>
);

Error.propTypes = {
  isWidthError: PropTypes.bool,
  isAuthError: PropTypes.bool,
  isAccessError: PropTypes.bool,
  isCommunityAccessError: PropTypes.bool,
  communityAccessMessage: PropTypes.string,
};

Error.defaultProps = {
  isWidthError: false,
  isAuthError: false,
  isAccessError: false,
  isCommunityAccessError: false,
  communityAccessMessage: null,
};

export default AppSwitchboard;
